import styled, { keyframes } from "styled-components";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInRight = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

export const BackLink = styled.div`
  z-index: 100;
  display: flex;
  margin: 20px 0px;
  align-items: center;
  position: relative;
  cursor: pointer;
  gap: 0.5rem;
  color: #ff6b35;
  font-weight: bold;
  padding: 7.5px 15px;
  border-radius: 25px;
  background: rgba(26, 26, 26, 0.8);
  border: 1px solid rgba(255, 107, 53, 0.3);
  text-decoration: none;
  width: fit-content;
  transition: 0.3s;
  &:hover {
    background: #ff6b35;
    color: #fff;
    transform: translateX(-5px);
  }
`;

export const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0rem 2rem 2rem 2rem;

  min-height: 100vh;
  position: relative;

  @media (max-width: 768px) {
    padding: 1rem;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="%23ff6b35" opacity="0.1"/><circle cx="75" cy="75" r="1.5" fill="%23ff6b35" opacity="0.1"/><circle cx="50" cy="10" r="0.8" fill="%23ff6b35" opacity="0.1"/><circle cx="10" cy="60" r="1.2" fill="%23ff6b35" opacity="0.1"/><circle cx="90" cy="30" r="0.9" fill="%23ff6b35" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
    z-index: 1;
  }
`;

// Loading and Error States
export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #fff;
`;

export const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #e74c3c;
`;

export const NotFoundContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #fff;
`;

// Feedback Message
export const FeedbackMessage = styled.div`
  position: fixed;
  top: 120px;
  right: 20px;
  background: ${(props) => (props.$type === "success" ? "#27ae60" : "#e74c3c")};
  color: #fff;
  padding: 1rem 1.5rem;
  border-radius: 10px;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  animation: ${slideInRight} 0.3s ease-out;
  max-width: 350px;
  font-size: 0.9rem;
  font-weight: 600;
`;

export const Breadcrumb = styled.nav`
  margin-bottom: 2rem;
  z-index: 2;
  position: relative;

  a,
  span {
    color: #cccccc;
    text-decoration: none;
    font-size: 0.9rem;

    &:hover {
      color: #ff6b35;
    }
  }

  .current {
    color: #ff6b35;
  }

  .separator {
    margin: 0 0.5rem;
    color: #666;
  }
`;

export const ProductLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 4rem;
  z-index: 2;
  position: relative;

  @media (max-width: 1024px) {
    gap: 3rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

export const GallerySection = styled.div`
  background: #1a1a1a;
  border-radius: 15px;
  padding: 2rem;
  border: 1px solid #333;
  height: fit-content;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

export const MainImage = styled.div`
  /* height: 500px; */
  background: linear-gradient(45deg, #333, #555);
  border-radius: 15px;
  margin-bottom: 1.5rem;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.02);
  }

  &:hover .zoom-overlay {
    opacity: 1;
  }

  .zoom-overlay {
    position: absolute;
    top: 20px;
    right: 20px;
    background: rgba(255, 107, 53, 0.9);
    color: white;
    padding: 0.8rem;
    border-radius: 50%;
    opacity: 0;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    aspect-ratio: 1 / 1;
    border-radius: 15px;
  }

  @media (max-width: 768px) {
    height: 400px;
  }

  @media (max-width: 480px) {
    height: 300px;
  }
`;

export const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    gap: 0.8rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const Thumbnail = styled.div`
  height: 100px;
  aspect-ratio: 1 / 1;
  background: linear-gradient(45deg, #333, #555);
  border-radius: 10px;
  cursor: pointer;
  border: 3px solid ${(props) => (props.$active ? "#ff6b35" : "#333")};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &:hover {
    border-color: #ff6b35;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 107, 53, 0.2);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 7px;
  }

  @media (max-width: 768px) {
    height: 80px;
  }

  @media (max-width: 480px) {
    height: 120px;
  }
`;

export const ProductInfo = styled.div`
  background: #1a1a1a;
  border-radius: 15px;
  padding: 2rem;
  border: 1px solid #333;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

export const BandTag = styled.span`
  background: #ff6b35;
  color: #ffffff;
  padding: 0.4rem 1.2rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: bold;
  display: inline-block;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const ProductTitle = styled.h1`
  color: #ffffff;
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

export const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;

  .stars {
    display: flex;
    gap: 0.2rem;
    color: #ff6b35;
  }

  .rating-text {
    color: #cccccc;
    font-size: 0.9rem;
  }
`;

export const PriceSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #2c2c2c 0%, #1e1e1e 100%);
  border-radius: 15px;
  border: 2px solid #ff6b35;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, #ff6b35, #e55a2b);
  }
`;

export const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

export const CurrentPrice = styled.span`
  color: #ff6b35;
  font-size: 2.2rem;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

export const OriginalPrice = styled.span`
  color: #999;
  text-decoration: line-through;
  font-size: 1.4rem;

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

export const DiscountBadge = styled.span`
  background: #e74c3c;
  color: #ffffff;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: bold;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
    100% {
      transform: scale(1);
    }
  }
`;

export const PriceNote = styled.small`
  color: #ccc;
`;

export const Description = styled.p`
  color: #cccccc;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

export const OptionsSection = styled.div`
  margin-bottom: 2rem;
`;

export const OptionLabel = styled.label`
  color: #ffffff;
  font-weight: bold;
  display: block;
  margin-bottom: 0.8rem;
  font-size: 1.1rem;
`;

export const RequiredIndicator = styled.span`
  color: #ff6b35;
  font-size: 0.8rem;
  margin-left: 0.5rem;
`;

export const StockIndicator = styled.span`
  color: #cccccc;
  font-size: 0.8rem;
  margin-left: 0.5rem;
`;

export const SizeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
  gap: 0.8rem;
  margin-bottom: 1.5rem;
  max-width: 400px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const SizeButton = styled.button`
  background: ${(props) => (props.$selected ? "#ff6b35" : "#2c2c2c")};
  color: #ffffff;
  border: 2px solid ${(props) => (props.$selected ? "#ff6b35" : "#444")};
  padding: 1rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: bold;
  max-height: 3rem;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #ff6b35;
    border-color: #ff6b35;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(255, 107, 53, 0.3);
  }

  &:disabled {
    background: #333;
    color: #666;
    cursor: not-allowed;
    border-color: #333;
    transform: none;
    box-shadow: none;
  }
`;

export const SizeGuideButton = styled.button`
  background: transparent;
  color: #ff6b35;
  border: 1px solid #ff6b35;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;

  &:hover {
    background: #ff6b35;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(255, 107, 53, 0.2);
  }
`;

export const QuantitySection = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-direction: column;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ActionsSection = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

export const AddToCartBtn = styled.button`
  flex: 1;
  background: linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%);
  color: #ffffff;
  border: none;
  padding: 1.2rem 2rem;
  border-radius: 12px;
  font-weight: bold;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover {
    background: linear-gradient(135deg, #e55a2b 0%, #ff6b35 100%);
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(255, 107, 53, 0.4);
  }

  &:hover::before {
    left: 100%;
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    font-size: 1rem;
  }
`;

export const WishlistBtn = styled.button`
  background: transparent;
  color: #ff6b35;
  border: 2px solid #ff6b35;
  padding: 1rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #ff6b35;
    color: #ffffff;
  }
`;

export const ShareBtn = styled.button`
  background: transparent;
  color: #cccccc;
  border: 2px solid #444;
  padding: 1.2rem;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #333;
    border-color: #ff6b35;
    color: #ff6b35;
    transform: translateY(-2px);
  }
`;

export const FeaturesList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
`;

export const Feature = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  color: #cccccc;
  font-size: 0.8rem;
  padding: 1rem;
  background: #2c2c2c;
  border-radius: 10px;
  border: 1px solid #333;
  max-width: 15rem;
  flex: 1;
  flex-direction: column;
  text-align: center;

  svg {
    color: #ff6b35;
    font-size: 1.4rem;
    flex-shrink: 0;
  }
`;

export const TabSection = styled.div`
  background: #1a1a1a;
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 3rem;
  border: 1px solid #333;
  z-index: 2;
  position: relative;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

export const TabNavigation = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 2rem;
  border-bottom: 1px solid #333;
  overflow-x: auto;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

export const TabButton = styled.button`
  background: transparent;
  color: ${(props) => (props.$active ? "#ff6b35" : "#cccccc")};
  border: none;
  padding: 1rem 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: bold;
  border-bottom: 2px solid
    ${(props) => (props.$active ? "#ff6b35" : "transparent")};
  white-space: nowrap;

  &:hover {
    color: #ff6b35;
  }

  @media (max-width: 480px) {
    text-align: left;
    border-bottom: none;
    border-left: 3px solid
      ${(props) => (props.$active ? "#ff6b35" : "transparent")};
  }
`;

export const TabContent = styled.div`
  color: #cccccc;
  line-height: 1.6;
  font-size: 1rem;
`;

// Tab content specific styles
export const TabHeading = styled.h3`
  color: #ff6b35;
  margin-bottom: 1rem;
`;

export const TabSubHeading = styled.h4`
  color: #fff;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
`;

export const TabList = styled.ul`
  list-style: none;
  padding: 0;
`;

export const TabListItem = styled.li`
  margin-bottom: 0.5rem;
`;

export const SpecTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const SpecRow = styled.tr`
  border-bottom: 1px solid #333;
`;

export const SpecCell = styled.td`
  padding: 0.5rem 0;
  font-weight: ${(props) => (props.$bold ? "bold" : "normal")};
`;

// Modal styles
export const ImageModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 15px;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: -20px;
  right: -20px;
  background: #ff6b35;
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: #e55a2b;
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    width: 35px;
    height: 35px;
    font-size: 1.2rem;
  }
`;

export const SimilarProductsSection = styled.div`
  z-index: 2;
  position: relative;
`;

export const SectionTitle = styled.h2`
  color: #ff6b35;
  font-size: 2rem;
  margin-bottom: 2rem;
  font-weight: bold;
`;

export const EmptyProductsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 429px;
  color: #cccccc;
  background: #1a1a1a;
  border-radius: 15px;
  border: 1px solid #333;
  width: 100%;
  text-align: center;
  padding: 2rem;
`;

export const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

export const CardInfo = styled.div`
  padding: 1.5rem;
`;

export const CardBandTag = styled.span`
  background: #ff6b35;
  color: #ffffff;
  padding: 0.2rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: bold;
  display: inline-block;
  margin-bottom: 0.5rem;
`;

export const CardTitle = styled.h3`
  color: #ffffff;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  font-weight: bold;
`;

export const CardPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .current {
    color: #ff6b35;
    font-size: 1.2rem;
    font-weight: bold;
  }

  .original {
    color: #999;
    text-decoration: line-through;
    font-size: 1rem;
  }
`;

/* PRODUCT SHOWCASE */
export const ProductShowcase = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  @media (max-width: 768px) {
  }
`;

export const ProductCard = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid #444;
  transition: all 0.3s ease;
  position: relative;
  height: fit-content;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;
  max-width: 19rem;
  width: 100%;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #ff6b35, #e55a2b);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(255, 107, 53, 0.3);
    border-color: #ff6b35;
  }
`;

export const ProductImageContainer = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
  background: #222;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

export const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

export const Badge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: ${({ variant }) =>
    variant === "discount"
      ? "#e74c3c"
      : variant === "limited"
      ? "#9b59b6"
      : variant === "new"
      ? "#27ae60"
      : "#ff6b35"};
  color: #ffffff;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: bold;
  z-index: 10;
`;

export const ProductHeader = styled.div`
  margin-bottom: 1rem;
`;

export const ProductBand = styled.span`
  color: #ff6b35;
  font-size: 0.9rem;
  font-weight: 600;
`;

export const ProductMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
`;

export const ProductMetaItem = styled.div`
  display: flex;
  align-items: center;
`;

export const ProductCategory = styled.span`
  background: rgba(255, 107, 53, 0.2);
  color: #ff6b35;
  padding: 0.3rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
`;

export const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const ProductPrice = styled.span`
  color: ${(props) => (props.$discounted ? "#888" : "#27ae60")};
  font-weight: bold;
  font-size: ${(props) => (props.$discounted ? "0.9rem" : "1.1rem")};
  text-decoration: ${(props) => (props.$discounted ? "line-through" : "none")};
`;

export const ProductDiscountPrice = styled.span`
  color: #27ae60;
  font-weight: bold;
  font-size: 1.1rem;
`;

export const ProductShortDesc = styled.p`
  color: #cccccc;
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0 0 1.5rem 0;
`;

/* SIZE SELECTOR */
export const ProductSizes = styled.div`
  color: #ff6b35;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  font-weight: bold;
`;

export const SizeSelector = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

export const SizeOption = styled.button`
  background: ${(props) => (props.$selected ? "#ff6b35" : "#444")};
  color: ${(props) => (props.$selected ? "#ffffff" : "#cccccc")};
  border: 1px solid ${(props) => (props.$selected ? "#ff6b35" : "#666")};
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.65rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: ${(props) => (props.$selected ? "bold" : "normal")};

  &:hover {
    background: ${(props) => (props.$selected ? "#e55a2b" : "#555")};
    border-color: #ff6b35;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #333;
    color: #666;
    border-color: #333;
  }
`;

/* QUANTITY CONTROLS */
export const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  background: #2c2c2c;
  border-radius: 10px;
  border: 2px solid #444;
  overflow: hidden;
  max-height: 2rem;
  width: fit-content;
`;

export const QuantityBtn = styled.button`
  background: transparent;
  color: #ffffff;
  border: none;
  padding: 1rem;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.3s ease;
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;

  &:hover {
    background: #ff6b35;
  }

  &:disabled {
    color: #666;
    cursor: not-allowed;
    background: transparent;
  }
`;

export const QuantityInput = styled.input`
  background: transparent;
  color: #ffffff;
  border: none;
  padding: 1rem;
  width: 55px;
  text-align: center;
  font-weight: bold;
  font-size: 0.8rem;

  &:focus {
    outline: none;
  }

  &:disabled {
    color: #666;
  }
`;

/* PRODUCT ACTIONS */
export const ProductActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

export const Button = styled.button`
  padding: 0.8rem 1.2rem;
  min-height: 3rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${(props) => {
    switch (props.$variant) {
      case "primary":
        return "#ff6b35";
      case "secondary":
        return "#666";
      case "danger":
        return "#e74c3c";
      case "success":
        return "#27ae60";
      default:
        return "#ff6b35";
    }
  }};
  color: #ffffff;

  &:hover {
    background: ${(props) => {
      switch (props.$variant) {
        case "primary":
          return "#e55a2b";
        case "secondary":
          return "#555";
        case "danger":
          return "#c0392b";
        case "success":
          return "#229954";
        default:
          return "#e55a2b";
      }
    }};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;
